
import React from 'react';
import { Ticket, Calendar, Copy, Check, ExternalLink } from 'lucide-react';
import TranslatedText from '@/TranslatedText';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';

// Example seasonal coupons data
const coupons = [
  {
    id: 1,
    code: 'AUTUMN25',
    title: 'Autumn Season Special',
    description: 'Get 25% off on all autumn collection items',
    validUntil: new Date('2025-11-30'),
    minSpend: 30000,
    maxDiscount: 50000,
    exclusions: 'Not valid on already discounted items',
    currency: 'RWF',
    category: 'Seasonal'
  },
  {
    id: 2,
    code: 'WELCOME20',
    title: 'New Customer Welcome',
    description: '20% discount on your first purchase',
    validUntil: new Date('2025-12-31'),
    minSpend: 15000,
    maxDiscount: 20000,
    exclusions: 'One-time use for new customers only',
    currency: 'RWF',
    category: 'New Users'
  },
  {
    id: 3,
    code: 'LOCAL15',
    title: 'Made in Rwanda Products',
    description: '15% off on all locally made products',
    validUntil: new Date('2025-10-15'),
    minSpend: 10000,
    maxDiscount: 30000,
    exclusions: 'Cannot be combined with other promotions',
    currency: 'RWF',
    category: 'Local Products'
  }
];

const SeasonalCoupons: React.FC = () => {
  const { toast } = useToast();
  const [copiedCode, setCopiedCode] = React.useState<string | null>(null);
  
  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast({
      title: 'Coupon code copied!',
      description: `${code} has been copied to your clipboard.`,
      duration: 3000,
    });
    
    setTimeout(() => {
      setCopiedCode(null);
    }, 3000);
  };
  
  return (
    <div className="py-12 border-t border-gray-200">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-ahavex-blue flex items-center">
          <Ticket className="mr-2 text-ahavex-gold" size={28} />
          <TranslatedText text="Seasonal Coupons" />
        </h2>
        <Button variant="outline" className="text-ahavex-blue border-ahavex-blue">
          <Calendar className="mr-2 h-4 w-4" />
          <TranslatedText text="View Calendar" />
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {coupons.map((coupon) => (
          <Card key={coupon.id} className="hover:shadow-md transition-shadow border-2 border-dashed border-gray-200">
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <span className="text-xs font-medium text-gray-500 block mb-1">
                    <TranslatedText text={coupon.category} />
                  </span>
                  <h3 className="text-lg font-bold text-ahavex-blue">
                    <TranslatedText text={coupon.title} />
                  </h3>
                </div>
                <div className="bg-gray-100 px-3 py-1 rounded text-xs">
                  <TranslatedText text="Valid until:" /> {coupon.validUntil.toLocaleDateString()}
                </div>
              </div>
              
              <p className="text-gray-600 mb-4">
                <TranslatedText text={coupon.description} />
              </p>
              
              <div className="bg-gray-50 p-3 rounded-lg mb-4 flex items-center justify-between">
                <code className="font-mono font-bold text-ahavex-blue text-lg">{coupon.code}</code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCopyCode(coupon.code)}
                  className="h-8"
                >
                  {copiedCode === coupon.code ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              
              <div className="text-xs text-gray-500 mb-4">
                <p>
                  <TranslatedText text="Min. spend:" /> {coupon.minSpend.toLocaleString()} {coupon.currency}
                </p>
                <p>
                  <TranslatedText text="Max. discount:" /> {coupon.maxDiscount.toLocaleString()} {coupon.currency}
                </p>
                <p>
                  <TranslatedText text="Exclusions:" /> <TranslatedText text={coupon.exclusions} />
                </p>
              </div>
              
              <Button className="w-full bg-ahavex-gold hover:bg-yellow-500 text-ahavex-blue">
                <TranslatedText text="Shop Now" />
                <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default SeasonalCoupons;
